import {cn} from '@/lib/utils';
import confetti from 'canvas-confetti';
import {Sparkles, X} from 'lucide-react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {DealSaleToast as DealSaleToastType} from './data';
import {formatPremium} from './data';

/* ─── Single Toast Card ─── */

function ToastCard({
    toast,
    onClose,
    isExiting,
    onAnimationEnd,
}: {
    toast: DealSaleToastType;
    onClose: (id: string) => void;
    isExiting: boolean;
    onAnimationEnd: (id: string) => void;
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const confettiFired = useRef(false);

    // Fire confetti on mount
    useEffect(() => {
        if (confettiFired.current) return;
        confettiFired.current = true;

        // Small delay so the card is visible first
        const timer = setTimeout(() => {
            const el = cardRef.current;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const x = (rect.left + rect.width / 2) / window.innerWidth;
            const y = (rect.top + rect.height / 2) / window.innerHeight;

            confetti({
                particleCount: 25,
                spread: 50,
                startVelocity: 20,
                gravity: 0.8,
                ticks: 80,
                origin: {x, y},
                colors: ['#456d69', '#5a8a85', '#e8edec', '#3a5b58', '#7fa9a5'],
                scalar: 0.7,
                zIndex: 1400,
                disableForReducedMotion: true,
            });
        }, 150);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div
            ref={cardRef}
            className={cn(
                'pointer-events-auto relative w-[280px] overflow-hidden rounded-xl border border-[#456d69]/20 shadow-lg',
                !isExiting && 'animate-in slide-in-from-left fade-in-0 duration-300 ease-out',
                isExiting && 'animate-out slide-out-to-left fade-out-0 duration-200 ease-in',
            )}
            style={{
                background: 'linear-gradient(135deg, #f8faf9 0%, #e8edec 40%, #f8faf9 100%)',
            }}
            onAnimationEnd={() => {
                if (isExiting) onAnimationEnd(toast.id);
            }}
        >
            {/* Left accent bar */}
            <div className="absolute inset-y-0 left-0 w-[3px] bg-gradient-to-b from-[#456d69] to-[#5a8a85]" />

            {/* Close button */}
            <button
                onClick={() => onClose(toast.id)}
                className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-200/50 hover:text-stone-600"
                aria-label="Close notification"
            >
                <X className="h-3 w-3" />
            </button>

            <div className="flex flex-col gap-0.5 py-2.5 pr-8 pl-4">
                {/* Header */}
                <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-[#456d69]" />
                    <span
                        className="text-[10px] uppercase tracking-wider text-[#456d69]"
                        style={{fontFamily: 'FredokaSemiBold, sans-serif'}}
                    >
                        New Sale!
                    </span>
                </div>

                {/* Agent name */}
                <span className="text-sm text-stone-900" style={{fontFamily: 'FredokaSemiBold, sans-serif'}}>
                    {toast.properties.agentName}
                </span>

                {/* Amount + stage */}
                <div className="flex items-center gap-1.5">
                    <span
                        className="text-base text-stone-900"
                        style={{fontFamily: 'LoraSemiBold, serif', fontVariantNumeric: 'tabular-nums'}}
                    >
                        {formatPremium(toast.properties.amount)}
                    </span>
                    <span className="text-stone-300">·</span>
                    <span className="text-xs text-stone-500" style={{fontFamily: 'FredokaMedium, sans-serif'}}>
                        {toast.properties.stageLabel}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ─── Toast Container ─── */

export default function DealSaleToastContainer({
    toasts,
    onRemove,
}: {
    toasts: DealSaleToastType[];
    onRemove: (id: string) => void;
}) {
    const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
    const dismissTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const handleClose = useCallback((id: string) => {
        // Clear auto-dismiss timer if manually closed
        const timer = dismissTimers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            dismissTimers.current.delete(id);
        }
        setExitingIds((prev) => new Set(prev).add(id));
    }, []);

    const handleAnimationEnd = useCallback(
        (id: string) => {
            setExitingIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            onRemove(id);
        },
        [onRemove],
    );

    // Auto-dismiss: schedule exit animation 5s after each toast appears
    useEffect(() => {
        for (const toast of toasts) {
            if (exitingIds.has(toast.id) || dismissTimers.current.has(toast.id)) continue;
            const timer = setTimeout(() => {
                dismissTimers.current.delete(toast.id);
                setExitingIds((prev) => new Set(prev).add(toast.id));
            }, 10000);
            dismissTimers.current.set(toast.id, timer);
        }

        // Clean up timers for toasts that were removed
        for (const [id, timer] of dismissTimers.current.entries()) {
            if (!toasts.some((t) => t.id === id)) {
                clearTimeout(timer);
                dismissTimers.current.delete(id);
            }
        }
    }, [toasts, exitingIds]);

    // Cleanup all timers on unmount
    useEffect(() => {
        return () => {
            for (const timer of dismissTimers.current.values()) {
                clearTimeout(timer);
            }
        };
    }, []);

    if (toasts.length === 0) return null;

    return (
        <div className="pointer-events-none fixed bottom-6 left-[72px] z-[1300] flex flex-col gap-2">
            {toasts.map((toast) => (
                <ToastCard
                    key={toast.id}
                    toast={toast}
                    onClose={handleClose}
                    isExiting={exitingIds.has(toast.id)}
                    onAnimationEnd={handleAnimationEnd}
                />
            ))}
        </div>
    );
}
